// SCR-STAFF-937 — دستورکارها (US-ORG-052 ↻): فهرست گزارش‌های خرابی شعبه با فیلتر وضعیت و شدت؛ چرخه کامل (AP-ORG-03): واگذاری به کارمند یا
// تأمین‌کننده (AC-ORG-052.1)، شروع، انتظار قطعه، ادامه، حل با شرح اقدام، هزینه و بازگشت دستگاه (AC-ORG-052.3)، بستن و بازگشایی و لغو؛ جزئیات با
// تاریخچه وضعیت، قرارداد فعال دستگاه (AC-ORG-034.2) و قطعات INV. زمان توقف محاسبه سرور (BR-ORG-054).
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { unwrap, type WorkOrderSeverity, type WorkOrderStatus, type WorkOrderView } from '@phoenix/api-client';
import { useAccess, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  CheckboxRHF,
  DataTable,
  EmptyState,
  faDigits,
  FormDialog,
  JalaliDate,
  ListPage,
  Money,
  MoneyFieldRHF,
  ReasonDialog,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  zRequiredText,
  type DataTableColumn,
  type StatusTone,
} from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { BranchFilter, useBranchSelection } from './common';
import { deviceKeys, SEVERITIES } from './deviceCommon';

export const SCR_STAFF_937 = 'SCR-STAFF-937';

const STATUSES: readonly WorkOrderStatus[] = ['Reported', 'Assigned', 'InProgress', 'WaitingForParts', 'Resolved', 'Closed', 'Cancelled'];

/** گذارهای ساده (بدون فرم) هر وضعیت؛ حل، واگذاری، لغو و بازگشایی دیالوگ دارند (SM-WorkOrder). */
const STEPS: Partial<Record<WorkOrderStatus, readonly ('start' | 'wait-for-parts' | 'resume' | 'close')[]>> = {
  Assigned: ['start'],
  InProgress: ['wait-for-parts'],
  WaitingForParts: ['resume'],
  Resolved: ['close'],
};
const RESOLVABLE: readonly WorkOrderStatus[] = ['Reported', 'Assigned', 'InProgress', 'WaitingForParts'];

function severityTone(severity: WorkOrderSeverity): StatusTone {
  return severity === 'Critical' ? 'error' : severity === 'High' ? 'pending' : severity === 'Medium' ? 'booked' : 'inactive';
}

export function WorkOrdersPage() {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('org.work_order.manage');
  const branches = useBranchSelection();
  const [params] = useSearchParams();
  const [status, setStatus] = useState<string>('Reported');
  const [severity, setSeverity] = useState<string>('');
  const [resolving, setResolving] = useState<WorkOrderView | null>(null);
  const [cancelling, setCancelling] = useState<WorkOrderView | null>(null);
  const [assigning, setAssigning] = useState<WorkOrderView | null>(null);
  const [reopening, setReopening] = useState<WorkOrderView | null>(null);
  const [details, setDetails] = useState<WorkOrderView | null>(null);
  const highlighted = params.get('id');
  const queryClient = useQueryClient();

  const orders = useQuery({
    queryKey: [...deviceKeys.workOrders(session?.tenantId), branches.branchId, status, severity],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/organization/work-orders', {
          params: {
            query: {
              branchId: branches.branchId,
              status: (status || undefined) as WorkOrderView['status'] | undefined,
              severity: (severity || undefined) as WorkOrderSeverity | undefined,
            },
          },
        }),
      ),
    enabled: Boolean(branches.branchId),
  });

  const cancel = useMutation({
    mutationFn: ({ order, note }: { order: WorkOrderView; note: string }) =>
      unwrap(tenantApi.POST('/api/v1/organization/work-orders/{id}:cancel', { params: { path: { id: order.id } }, body: { note } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deviceKeys.workOrders(session?.tenantId) });
      setCancelling(null);
    },
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: deviceKeys.workOrders(session?.tenantId) });
    void queryClient.invalidateQueries({ queryKey: deviceKeys.devices(session?.tenantId) });
  };
  const step = useMutation({
    mutationFn: ({ order, action, note }: { order: WorkOrderView; action: 'start' | 'wait-for-parts' | 'resume' | 'close' | 'reopen'; note?: string }) => {
      const options = { params: { path: { id: order.id } }, body: { note: note ?? null } };
      switch (action) {
        case 'start':
          return unwrap(tenantApi.POST('/api/v1/organization/work-orders/{id}:start', options));
        case 'wait-for-parts':
          return unwrap(tenantApi.POST('/api/v1/organization/work-orders/{id}:wait-for-parts', options));
        case 'resume':
          return unwrap(tenantApi.POST('/api/v1/organization/work-orders/{id}:resume', options));
        case 'close':
          return unwrap(tenantApi.POST('/api/v1/organization/work-orders/{id}:close', options));
        default:
          return unwrap(tenantApi.POST('/api/v1/organization/work-orders/{id}:reopen', options));
      }
    },
    onSuccess: () => {
      refresh();
      setReopening(null);
    },
  });

  const columns: DataTableColumn<WorkOrderView>[] = [
    {
      id: 'number',
      headerKey: 'org:work_orders.columns.number',
      cell: (row) => (
        <Button size="small" onClick={() => setDetails(row)} sx={{ fontWeight: row.id === highlighted ? 700 : undefined }} data-testid={`work-order-${row.number}`}>
          <bdi dir="ltr">{row.number}</bdi>
        </Button>
      ),
    },
    { id: 'resource', headerKey: 'org:work_orders.columns.resource', cell: (row) => `${row.resourceName ?? '—'} (${t(`org:resource_kind.${row.resourceKind}`)})` },
    { id: 'severity', headerKey: 'org:work_orders.columns.severity', cell: (row) => <StatusChip tone={severityTone(row.severity)} labelKey={`org:severity.${row.severity}`} /> },
    { id: 'description', headerKey: 'org:work_orders.columns.description', cell: (row) => row.description },
    {
      id: 'assignee',
      headerKey: 'org:work_orders.columns.assignee',
      cell: (row) => row.supplierName ?? (row.assigneeMembershipId ? t('org:work_orders.staff_assignee') : '—'),
    },
    { id: 'reported', headerKey: 'org:work_orders.columns.reported', cell: (row) => <JalaliDate value={row.reportedAt} withTime /> },
    {
      id: 'outcome',
      headerKey: 'org:work_orders.columns.outcome',
      cell: (row) =>
        row.status === 'Resolved' || row.status === 'Closed' ? (
          <Stack spacing={0.5}>
            <span>{t('org:work_orders.downtime', { n: faDigits(Number(row.downtimeMinutes ?? 0)) })}</span>
            {row.cost && <Money amountMinor={Number(row.cost.amount)} />}
          </Stack>
        ) : (
          <span data-testid={`work-order-status-${row.number}`}>{t(`org:work_order_status.${row.status}`)}</span>
        ),
    },
    {
      id: 'actions',
      headerKey: 'org:work_orders.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {(row.status === 'Reported' || row.status === 'Assigned') && (
            <Button size="small" onClick={() => setAssigning(row)} disabled={!write.canWrite} data-testid={`work-order-assign-${row.number}`}>
              {t('org:work_orders.assign')}
            </Button>
          )}
          {(STEPS[row.status] ?? []).map((action) => (
            <Button key={action} size="small" onClick={() => step.mutate({ order: row, action })} disabled={!write.canWrite || step.isPending} data-testid={`work-order-${action}-${row.number}`}>
              {t(`org:work_orders.actions.${action}`)}
            </Button>
          ))}
          {RESOLVABLE.includes(row.status) && (
            <Button size="small" onClick={() => setResolving(row)} disabled={!write.canWrite} data-testid={`work-order-resolve-${row.number}`}>
              {t('org:work_orders.resolve')}
            </Button>
          )}
          {row.status === 'Resolved' && (
            <Button size="small" color="warning" onClick={() => setReopening(row)} disabled={!write.canWrite} data-testid={`work-order-reopen-${row.number}`}>
              {t('org:work_orders.actions.reopen')}
            </Button>
          )}
          {(row.status === 'Reported' || row.status === 'Assigned') && (
            <Button size="small" color="error" onClick={() => setCancelling(row)} disabled={!write.canWrite} data-testid={`work-order-cancel-${row.number}`}>
              {t('org:work_orders.cancel')}
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_937}
      titleKey="org:work_orders.title"
      subtitleKey="org:work_orders.subtitle"
      filters={
        <Stack spacing={1}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <BranchFilter options={branches.options} value={branches.branchId} onChange={branches.setBranchId} />
            <TextField select size="small" label={t('org:work_orders.filters.status')} value={status} onChange={(event) => setStatus(event.target.value)} sx={{ minWidth: 160 }} slotProps={{ htmlInput: { 'data-testid': 'work-order-filter-status' } }}>
              <MenuItem value="">{t('org:rooms.filters.all')}</MenuItem>
              {STATUSES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`org:work_order_status.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label={t('org:work_orders.filters.severity')} value={severity} onChange={(event) => setSeverity(event.target.value)} sx={{ minWidth: 160 }} slotProps={{ htmlInput: { 'data-testid': 'work-order-filter-severity' } }}>
              <MenuItem value="">{t('org:rooms.filters.all')}</MenuItem>
              {SEVERITIES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`org:severity.${value}`)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <WriteNotice write={write} />
          {step.error ? <Alert severity="error">{t('org:work_orders.step_failed')}</Alert> : null}
        </Stack>
      }
    >
      {branches.options.length === 0 ? (
        <EmptyState titleKey="org:no_branch" />
      ) : (
        <DataTable
          captionKey="org:work_orders.caption"
          columns={columns}
          rows={orders.data?.items}
          getRowId={(row) => row.id}
          isLoading={orders.isPending}
          error={orders.error}
          onRetry={() => void orders.refetch()}
          emptyTitleKey="org:work_orders.empty"
        />
      )}
      {resolving && <ResolveDialog order={resolving} onClose={() => setResolving(null)} />}
      {assigning && <AssignDialog order={assigning} onClose={() => setAssigning(null)} onSaved={refresh} />}
      {details && <WorkOrderDetailsDialog id={details.id} onClose={() => setDetails(null)} />}
      <ReasonDialog
        open={reopening !== null}
        titleKey="org:work_orders.reopen_title"
        confirmKey="org:work_orders.actions.reopen"
        destructive={false}
        busy={step.isPending}
        onConfirm={(note) => reopening && step.mutate({ order: reopening, action: 'reopen', note })}
        onCancel={() => setReopening(null)}
        error={step.error ?? undefined}
      />
      <ReasonDialog
        open={cancelling !== null}
        titleKey="org:work_orders.cancel_title"
        confirmKey="org:work_orders.cancel"
        busy={cancel.isPending}
        onConfirm={(note) => cancelling && cancel.mutate({ order: cancelling, note })}
        onCancel={() => setCancelling(null)}
        error={cancel.error ?? undefined}
      />
    </ListPage>
  );
}

const resolveSchema = z.object({
  note: zRequiredText(2000),
  cost: z.number().int().nonnegative().nullable(),
  restoreDevice: z.boolean(),
});
type ResolveForm = z.input<typeof resolveSchema>;

function ResolveDialog({ order, onClose }: { order: WorkOrderView; onClose: () => void }) {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const { control, handleSubmit, formState } = useForm<ResolveForm>({
    resolver: zodResolver(resolveSchema),
    defaultValues: { note: '', cost: null, restoreDevice: true },
  });
  const resolve = useMutation({
    mutationFn: (form: ResolveForm) =>
      unwrap(
        tenantApi.POST('/api/v1/organization/work-orders/{id}:resolve', {
          params: { path: { id: order.id } },
          body: {
            note: form.note,
            cost: form.cost !== null && form.cost !== undefined ? { amount: form.cost, currency: 'IRR' } : null,
            restoreDevice: form.restoreDevice,
          },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deviceKeys.workOrders(session?.tenantId) });
      void queryClient.invalidateQueries({ queryKey: deviceKeys.devices(session?.tenantId) });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      screenId="SCR-STAFF-937-RESOLVE"
      titleKey="org:work_orders.resolve_title"
      onSubmit={() => void handleSubmit((form) => resolve.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={resolve.isPending}
      submitError={resolve.error ?? undefined}
    >
      <TextFieldRHF control={control} name="note" labelKey="org:work_orders.form.note" required fullWidth multiline minRows={3} slotProps={{ htmlInput: { 'data-testid': 'work-order-note' } }} />
      <MoneyFieldRHF control={control} name="cost" labelKey="org:work_orders.form.cost" />
      {order.resourceKind === 'Device' && <CheckboxRHF control={control} name="restoreDevice" labelKey="org:work_orders.form.restore_device" />}
    </FormDialog>
  );
}

const assignSchema = z.object({
  target: z.enum(['staff', 'supplier']),
  assigneeMembershipId: z.string(),
  supplierId: z.string(),
  note: z.string().max(2000),
});
type AssignForm = z.input<typeof assignSchema>;

/** واگذاری به کارمند (عضو IAM شعبه) یا تأمین‌کننده خدمات فنی INV (AC-ORG-052.1؛ PHX-ORG-0702، 0314). */
function AssignDialog({ order, onClose, onSaved }: { order: WorkOrderView; onClose: () => void; onSaved: () => void }) {
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const members = useQuery({
    queryKey: ['identity', 'memberships', session?.tenantId, 'work-order-assign'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/identity/memberships', { params: { query: { limit: 100 } } })),
    enabled: access.can('iam.member.view'),
  });
  const suppliers = useQuery({
    queryKey: ['inventory', 'suppliers', session?.tenantId, 'technical'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/suppliers', { params: { query: { active: true, limit: 100 } } })),
    enabled: access.can('inv.supplier.manage'),
  });
  const schema = assignSchema.superRefine((value, ctx) => {
    if (value.target === 'staff' && !value.assigneeMembershipId) {
      ctx.addIssue({ code: 'custom', path: ['assigneeMembershipId'], message: 'common:validation.required' });
    }
    if (value.target === 'supplier' && !value.supplierId) {
      ctx.addIssue({ code: 'custom', path: ['supplierId'], message: 'common:validation.required' });
    }
  });
  const { control, handleSubmit, formState, watch } = useForm<AssignForm>({
    resolver: zodResolver(schema),
    defaultValues: { target: 'staff', assigneeMembershipId: order.assigneeMembershipId ?? '', supplierId: order.supplierId ?? '', note: '' },
  });
  const target = watch('target');
  const assign = useMutation({
    mutationFn: (form: AssignForm) =>
      unwrap(
        tenantApi.POST('/api/v1/organization/work-orders/{id}:assign', {
          params: { path: { id: order.id } },
          body: {
            assigneeMembershipId: form.target === 'staff' ? form.assigneeMembershipId : null,
            supplierId: form.target === 'supplier' ? form.supplierId : null,
            note: form.note.trim() || null,
          },
        }),
      ),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const memberOptions = (members.data?.items ?? [])
    .filter((item) => item.status === 'Active')
    .map((item) => ({ value: item.membershipId, label: item.displayName ?? item.mobile ?? item.membershipId }));
  const supplierOptions = (suppliers.data?.items ?? []).filter((item) => item.type === 'TechnicalServices' || item.type === 'Both').map((item) => ({ value: item.id, label: item.name }));
  return (
    <FormDialog open screenId="SCR-STAFF-937-ASSIGN" titleKey="org:work_orders.assign_title" onSubmit={() => void handleSubmit((form) => assign.mutate(form))()} onClose={onClose} isDirty={formState.isDirty} isSubmitting={assign.isPending} submitError={assign.error ?? undefined}>
      <SelectRHF control={control} name="target" labelKey="org:work_orders.form.target" required fullWidth options={[{ value: 'staff', labelKey: 'org:work_orders.form.target_staff' }, { value: 'supplier', labelKey: 'org:work_orders.form.target_supplier' }]} />
      {target === 'staff' ? (
        <SelectRHF control={control} name="assigneeMembershipId" labelKey="org:work_orders.form.assignee" required fullWidth options={memberOptions} />
      ) : (
        <SelectRHF control={control} name="supplierId" labelKey="org:work_orders.form.supplier" required fullWidth options={supplierOptions} />
      )}
      <TextFieldRHF control={control} name="note" labelKey="org:work_orders.form.assign_note" fullWidth multiline minRows={2} />
    </FormDialog>
  );
}

/** جزئیات دستور کار: تاریخچه وضعیت، قرارداد فعال (AC-ORG-034.2) و قطعات صادره INV. */
function WorkOrderDetailsDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const order = useQuery({
    queryKey: [...deviceKeys.workOrders(session?.tenantId), 'detail', id],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/work-orders/{id}', { params: { path: { id } } })),
  });
  const data = order.data;
  return (
    <FormDialog open screenId="SCR-STAFF-937-DETAIL" titleKey="org:work_orders.details_title" onSubmit={onClose} onClose={onClose} isDirty={false} isSubmitting={false} submitKey="common:actions.close" maxWidth="md">
      {data && (
        <Stack spacing={2}>
          <Typography variant="body2">
            <bdi dir="ltr">{data.number}</bdi> — {data.resourceName ?? ''} — {t(`org:work_order_status.${data.status}`)}
          </Typography>
          {data.activeContract ? (
            <Alert severity="info" data-testid="work-order-contract">
              {t('org:work_orders.active_contract', { kind: t(`org:contract_kind.${data.activeContract.kind}`), party: data.activeContract.partyName })} <JalaliDate value={data.activeContract.endsOn} />
            </Alert>
          ) : null}
          {Number(data.partsCost?.amount ?? 0) > 0 && (
            <Typography variant="body2" data-testid="work-order-parts">
              {t('org:work_orders.parts_cost')} <Money amountMinor={Number(data.partsCost?.amount ?? 0)} />
            </Typography>
          )}
          <Table size="small" aria-label={t('org:work_orders.history')}>
            <TableBody>
              {(data.events ?? []).map((event) => (
                <TableRow key={event.id}>
                  <TableCell>
                    <JalaliDate value={event.at} withTime />
                  </TableCell>
                  <TableCell>{t(`org:work_order_status.${event.toStatus}`)}</TableCell>
                  <TableCell>{event.note ?? ''}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Stack>
      )}
    </FormDialog>
  );
}
